import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  X,
  Check,
  CheckCheck,
  AlertCircle,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

const NotificationCenter = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [archived, setArchived] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    if (token && open) {
      loadNotifications();
      loadArchived();
    }
  }, [token, open]);

  // Periodically refresh notifications while the app is visible (reduced frequency)
  useEffect(() => {
    if (!token) return;

    const loadIfVisible = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications();
      }
    };

    // Initial load when token becomes available and tab is visible
    loadIfVisible();

    // Poll every 2 minutes instead of every 30 seconds
    const interval = setInterval(loadIfVisible, 120000);

    // Also refresh when the tab becomes visible or window gains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications();
      }
    };

    window.addEventListener('focus', loadIfVisible);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', loadIfVisible);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  const loadNotifications = async () => {
    if (!token) return;
    try {
      const response = await api.notificationsGet();
      setNotifications(response.notifications || []);
      setUnreadCount(response.unreadCount || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const loadArchived = async () => {
    if (!token) return;
    try {
      const res = await api.notificationsArchived(5, 0);
      setArchived(Array.isArray(res?.items) ? res.items : []);
    } catch (error) {
      console.error('Error loading archived notifications:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.notificationMarkRead(id);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Konnte nicht als gelesen markieren',
        variant: 'destructive',
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      await api.notificationMarkAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
      toast({ title: 'Alle als gelesen markiert' });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Fehler beim Markieren',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (id) => {
    try {
      // Optimistically move the item to archive preview
      const removed = notifications.find(n => n.id === id) || null;
      if (removed) {
        const archivedItem = { ...removed, archivedAt: new Date().toISOString() };
        setArchived(prev => [archivedItem, ...prev].slice(0, 5));
      }

      await api.notificationDelete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      // After archive, refresh archived preview list
      loadArchived();
      toast({ title: t('common.alerts.notificationDeleted') });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.alerts.couldNotDelete'),
        variant: 'destructive',
      });
    }
  };

  const openDetail = async (id) => {
    try {
      const n = await api.notificationGet(id);
      setDetail(n);
      setDetailOpen(true);
    } catch (error) {
      console.error('Error loading notification detail:', error);
      toast({ title: 'Fehler', description: 'Details konnten nicht geladen werden', variant: 'destructive' });
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'SUCCESS':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'WARNING':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'ERROR':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'ACTION_REQUIRED':
        return <AlertCircle className="w-5 h-5 text-blue-600" />;
      default:
        return <Info className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      URGENT: 'bg-red-100 text-red-800 border-red-300',
      HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
      MEDIUM: 'bg-blue-100 text-blue-800 border-blue-300',
      LOW: 'bg-muted text-foreground border-border',
    };
    
    if (priority === 'LOW') return null; // Don't show LOW priority badge
    
    return (
      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${colors[priority] || colors.MEDIUM}`}>
        {priority}
      </span>
    );
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffInHours = Math.floor((now - notifDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMins = Math.floor((now - notifDate) / (1000 * 60));
      return diffInMins <= 1 ? 'Gerade eben' : `vor ${diffInMins} Min`;
    }
    if (diffInHours < 24) return `vor ${diffInHours} Std`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Gestern';
    if (diffInDays < 7) return `vor ${diffInDays} Tagen`;
    return notifDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 hover:bg-muted rounded-lg transition-all">
          <Bell className="w-5 h-5 text-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-semibold text-lg">Benachrichtigungen</h3>
            <p className="text-xs text-muted-foreground">{unreadCount} ungelesen</p>
          </div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                disabled={loading}
                title="Alle als gelesen markieren"
              >
                <CheckCheck className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-96">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Bell className="w-12 h-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground font-medium">{t('redeemer.none')}</p>
              <p className="text-xs text-muted-foreground mt-1">Du bist auf dem neuesten Stand!</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-muted transition-colors ${
                    !notification.isRead ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <button
                          onClick={() => openDetail(notification.id)}
                          className="font-medium text-sm text-left text-foreground line-clamp-1 hover:underline"
                          title="Details anzeigen"
                        >
                          {notification.title}
                        </button>
                        {getPriorityBadge(notification.priority)}
                      </div>
                      <button
                        onClick={() => openDetail(notification.id)}
                        className="text-sm text-muted-foreground text-left line-clamp-2 mb-2 hover:underline"
                        title="Details anzeigen"
                      >
                        {notification.message}
                      </button>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{formatTime(notification.createdAt)}</span>
                          {notification.senderName && (
                            <>
                              <span>•</span>
                              <span className="font-medium text-muted-foreground">
                                von {notification.senderName}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-muted-foreground">
                          {new Date(notification.createdAt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="flex items-center gap-1">
                          {notification.actionUrl && notification.actionLabel && (
                            <a
                              href={notification.actionUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              onClick={() => markAsRead(notification.id)}
                            >
                              {notification.actionLabel}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {!notification.isRead && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="Als gelesen markieren"
                            >
                              <Check className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 hover:bg-red-100 rounded transition-colors"
                            title={t('merchant.campaigns.delete')}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Archived preview */}
              <div className="p-3 bg-muted">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Archiv (letzte 5)
                  </div>
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        const isSystemOwner = user?.role === 'SYSTEM_OWNER' || isStaff;
                        if (isSystemOwner) {
                          navigate('/admin/notifications');
                        } else {
                          navigate('/notifications/archive');
                        }
                        setOpen(false);
                      }}
                    >
                      Mehr
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {archived.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => openDetail(n.id)}
                      className="w-full text-left p-2 border rounded hover:bg-background transition flex gap-3"
                    >
                      <div className="mt-0.5">
                        {getIcon(n.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-xs text-foreground line-clamp-1">{n.title}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {new Date(n.archivedAt || n.createdAt).toLocaleString('de-DE')}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                      </div>
                    </button>
                  ))}
                  {!archived.length && (
                    <div className="text-xs text-muted-foreground">{t('notifications.archive.none')}</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl">
            {detail ? (
              <>
                <DialogHeader>
                  <DialogTitle>{detail.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    {new Date(detail.createdAt).toLocaleString('de-DE')}
                    {detail.senderName ? ` – Absender: ${detail.senderName}` : ''}
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-foreground">{detail.message}</div>
                  {detail.actionUrl && detail.actionLabel && (
                    <div>
                      <a
                        href={detail.actionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                      >
                        {detail.actionLabel}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Lade…</div>
            )}
          </DialogContent>
        </Dialog>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
